/**
 * Shared navigation configuration — Sentinel
 * Mirrors Global's pattern: groups with collapsible sections
 * Permissions filtered at render time
 *
 * Group order matches Global exactly so users see the same sidebar.
 * Groups not yet ported (peaceOfSleep, email, reports) are omitted —
 * they will appear in the same position once migrated.
 */
import { STAFF_ASSISTANT_NAME } from './assistant';

export interface NavItem {
  path: string;
  label: string;
  icon: string;
  end?: boolean;
  /** A small heading inside its section; items with the same sub sit together under it. */
  sub?: string;
}

export interface NavGroup {
  title: string;
  icon: string;
  items: NavItem[];
}

export const navGroups: Record<string, NavGroup> = {
  // One side menu in sections by job (Gary, 1 Nov 2026: no tabs, one list, sections fold away,
  // pinned pages and search on top). Every page keeps its own address; only the grouping changed.
  // The order here is the order on screen.
  work: {
    title: 'Work',
    icon: '⚡',
    items: [
      { path: '/to-do', label: 'To Do', icon: '✅', end: true },
      // CMB Chats inside Sentinel (Gary, 18 Oct): shown where WhatsApp is switched on (AdminLayout).
      { path: '/inbox', label: 'Chats', icon: '💬' },
      { path: '/customers', label: 'Customers', icon: '👤' },
      { path: '/bookings', label: 'Bookings', icon: '📅', end: true },
      { path: '/service-logs', label: 'Service Logs', icon: '📋', end: true },
      { path: '/certification', label: 'Service Certificates', icon: '📜' },
    ],
  },
  sales: {
    title: 'Sales',
    icon: '🎯',
    items: [
      { path: '/leads', label: 'Leads', icon: '🎯' },
      { path: '/proposals', label: 'Proposals', icon: '📄', end: true },
      { path: '/training-courses', label: 'Courses', icon: '🎯', sub: 'Coaching' },
      { path: '/training-modules', label: 'Modules', icon: '📚', sub: 'Coaching' },
      { path: '/training-assessments', label: 'Assessments', icon: '📝', sub: 'Coaching' },
      { path: '/training-trainees', label: 'Trainees', icon: '👥', sub: 'Coaching' },
      { path: '/training-content', label: 'Content', icon: '📄', sub: 'Coaching' },
    ],
  },
  peaceOfSleep: {
    title: 'Peace of Sleep',
    icon: '🛏️',
    items: [
      { path: '/pos-leads', label: 'Leads', icon: '🎯' },
      { path: '/pos-proposals', label: 'Proposals', icon: '📄' },
      { path: '/pos-properties', label: 'Portfolio', icon: '🏨' },
      { path: '/pos-ambassadors', label: 'Ambassadors', icon: '🤝' },
      { path: '/charters', label: 'Charters', icon: '🔑' },
    ],
  },
  marketing: {
    title: 'Marketing',
    icon: '📣',
    items: [
      { path: '/marketing/studio', label: 'Creative Studio', icon: '🎨' },
      { path: '/marketing/campaigns', label: 'Campaigns', icon: '📣', end: true },
      { path: '/marketing/social-posts', label: 'Social posts', icon: '📝' },
      { path: '/marketing/reputation', label: 'Reputation', icon: '⭐' },
      { path: '/marketing/social-inbox', label: 'Social inbox', icon: '💬' },
      { path: '/marketing/landing-pages', label: 'Landing pages', icon: '🧭' },
      { path: '/marketing/trackers', label: 'Campaign trackers', icon: '📊' },
      { path: '/marketing/spend', label: 'Spend & Recovery', icon: '💰' },
      { path: '/email/customers', label: 'Customer emails', icon: '👤', sub: 'Email' },
      { path: '/email/leads', label: 'Lead emails', icon: '🎯', sub: 'Email' },
      { path: '/email/pos', label: 'Peace of Sleep emails', icon: '🛏️', sub: 'Email' },
      { path: '/content-library', label: 'Content Library', icon: '📚', end: true, sub: 'Assets and offers' },
      { path: '/content-library/review', label: 'Review Queue', icon: '✅', sub: 'Assets and offers' },
      { path: '/rewards/codes', label: 'Codes', icon: '🏷️', sub: 'Assets and offers' },
      { path: '/collaborations', label: 'Collaborations', icon: '🤝', sub: 'Assets and offers' },
    ],
  },
  finance: {
    title: 'Money',
    icon: '💰',
    items: [
      { path: '/invoices', label: 'Invoices', icon: '🧾' },
      { path: '/items', label: 'Items (price list)', icon: '📦' },
      { path: '/payouts', label: 'Payouts', icon: '💸' },
      { path: '/vendors', label: 'Vendors', icon: '🏭' },
      { path: '/splits', label: 'Split Codes', icon: '💰' },
      { path: '/rewards', label: 'Rewards dashboard', icon: '📊', end: true, sub: 'Rewards' },
      { path: '/rewards/redemptions', label: 'Redemptions', icon: '✅', sub: 'Rewards' },
      { path: '/rewards/credits', label: 'Customer Credits', icon: '💳', sub: 'Rewards' },
      { path: '/rewards/settlements', label: 'Partner Invoicing', icon: '🏦', sub: 'Rewards' },
    ],
  },
  reports: {
    title: 'Reports',
    icon: '📊',
    items: [
      { path: '/reports/sales-performance', label: 'Sales Performance', icon: '📈' },
      { path: '/reports/lead-performance', label: 'Lead Performance', icon: '🎯' },
      { path: '/reports/customer-survey', label: 'Customer Survey', icon: '📝' },
      { path: '/reports/whatsapp', label: 'WhatsApp', icon: '💬' },
      { path: '/reports/transactions', label: 'Service Transactions', icon: '📋', sub: 'Money' },
      { path: '/reports/vendor-statements', label: 'Vendor Statements', icon: '🧾', sub: 'Money' },
      { path: '/reports/invoice-splits', label: 'Customer Invoice Splits', icon: '🔀', sub: 'Money' },
      { path: '/reports/equipment-register', label: 'Equipment Register', icon: '🔧', sub: 'Equipment' },
    ],
  },
  admin: {
    title: 'Admin',
    icon: '⚙️',
    items: [
      { path: '/users', label: 'Users', icon: '👤' },
      { path: '/candidates', label: 'Candidates', icon: '👷' },
      { path: '/contracts', label: 'Contracts', icon: '📋' },
      { path: '/territories', label: 'Territories', icon: '🏢' },
      { path: '/regions', label: 'Regions', icon: '🗺️' },
      { path: '/equipment', label: 'Equipment', icon: '🔧', sub: 'Set-up' },
      { path: '/booking-settings', label: 'Booking Settings', icon: '📅', sub: 'Set-up' },
      { path: '/system-comms', label: 'System Comms', icon: '💬', sub: 'Set-up' },
      { path: '/email/system', label: 'System Emails', icon: '✉️', sub: 'Set-up' },
      { path: '/email/candidates', label: 'Candidate Emails', icon: '👷', sub: 'Set-up' },
      // WhatsApp settings (Gary, 15 Oct): super admins only until operators are onboarded (no permission key, so hidden).
      { path: '/whatsapp', label: 'WhatsApp settings', icon: '💬', sub: 'Set-up' },
    ],
  },
  superAdmin: {
    title: 'Super Admin',
    icon: '🔒',
    items: [
      { path: '/permission-profiles', label: 'Permission Profiles', icon: '🛡️' },
      { path: '/config', label: 'Platform Config', icon: '⚙️' },
      { path: '/territory-purchases', label: 'Territory Purchases', icon: '🏘️' },
      { path: '/system-revenue', label: 'System Revenue', icon: '🏛️' },
      { path: '/ask-admin', label: 'AI team', icon: '🧠' },
    ],
  },
  // Help carries no permission key on purpose. Every user sees it, because the
  // guides explain what things cost, which is exactly what an operator needs.
  help: {
    title: 'Help',
    icon: '❓',
    items: [
      { path: '/help', label: 'Guides', icon: '📖', end: true },
    ],
  },
};

// All navigation items flattened (for page title matching)
// Includes sub-routes so the topbar title resolves correctly on detail/form pages
export const allNavItems: NavItem[] = [
  { path: '/', label: 'Home', icon: '🏠', end: true },
  ...Object.values(navGroups).flatMap((g) => g.items || []),
  // Sub-routes — title inherits from parent
  { path: '/service-logs/new', label: 'Service Logs', icon: '📋' },
  { path: '/service-logs/', label: 'Service Logs', icon: '📋' },
  { path: '/invoices/', label: 'Invoices', icon: '🧾' },
  { path: '/customers/', label: 'Customers', icon: '👤' },
  { path: '/bookings/', label: 'Bookings', icon: '📅' },
  { path: '/content-library', label: 'Content Library', icon: '📚' },
  { path: '/content-library/submit', label: 'Content Library', icon: '📚' },
  { path: '/content-library/review', label: 'Review Queue', icon: '✅' },
  { path: '/rewards', label: 'Rewards', icon: '🎁' },
  { path: '/rewards/', label: 'Rewards', icon: '🎁' },
  { path: '/rewards/codes', label: 'Codes', icon: '🏷️' },
  { path: '/rewards/redemptions', label: 'Redemptions', icon: '✅' },
  { path: '/rewards/vouchers', label: 'Discount Vouchers', icon: '🎟️' },
  { path: '/rewards/credits', label: 'Customer Credits', icon: '💳' },
  { path: '/rewards/settlements', label: 'Partner Invoicing', icon: '🏦' },
  { path: '/rewards/pool-ledger', label: 'Pool Ledger', icon: '📒' },
  { path: '/rewards/program-rules', label: 'Program Rules', icon: '📐' },
  { path: '/booking-settings', label: 'Booking Settings', icon: '📅' },
  { path: '/my-availability', label: 'My Availability', icon: '📅' },
  { path: '/vendors', label: 'Vendors', icon: '🏭' },
  { path: '/payouts', label: 'Payouts', icon: '💸' },
  { path: '/system-revenue', label: 'System Revenue', icon: '🏛️' },
  { path: '/email/system', label: 'System Emails', icon: '✉️' },
  { path: '/system-comms', label: 'System Comms', icon: '💬' },
  { path: '/permission-profiles', label: 'Permission Profiles', icon: '🛡️' },
  // Training Academy sub-routes
  { path: '/training-trainees', label: 'Trainees', icon: '👥' },
  { path: '/training-courses', label: 'Courses', icon: '🎯' },
  { path: '/training-modules', label: 'Modules', icon: '📚' },
  { path: '/training-assessments', label: 'Assessments', icon: '📝' },
  { path: '/training-content', label: 'Content', icon: '📄' },
  // Certification sub-routes
  { path: '/certification', label: 'Service Certificates', icon: '📜' },
  { path: '/certification/submit', label: 'Service Certificates', icon: '📜' },
  // Leads + Estimator + Proposals sub-routes
  { path: '/leads', label: 'Leads', icon: '🎯' },
  { path: '/candidates', label: 'Candidates', icon: '👷' },
  { path: '/estimator', label: 'Estimator', icon: '🧮' },
  { path: '/proposals', label: 'Proposals', icon: '📄' },
  // Marketing Hub
  { path: '/marketing/studio', label: 'Creative Studio', icon: '🎨' },
  { path: '/marketing/campaigns', label: 'Campaigns', icon: '📣' },
  { path: '/marketing/social-posts', label: 'Social posts', icon: '📝' },
  { path: '/marketing/reputation', label: 'Reputation', icon: '⭐' },
  { path: '/marketing/social-inbox', label: 'Social inbox', icon: '💬' },
  { path: '/marketing/landing-pages', label: 'Landing pages', icon: '🧭' },
  { path: '/marketing/trackers', label: 'Campaign trackers', icon: '📊' },
  { path: '/marketing/spend', label: 'Spend & Recovery', icon: '💰' },
  // Help
  { path: '/help', label: 'Guides', icon: '📖' },
  { path: '/ask', label: STAFF_ASSISTANT_NAME, icon: '🧠' },
  { path: '/inbox', label: 'Chats', icon: '💬' },
  { path: '/help/codes', label: 'How codes work', icon: '📖' },
  { path: '/help/tiers', label: 'Customer tiers', icon: '📖' },
  { path: '/help/posting-rhythm', label: 'Our posting rhythm', icon: '📖' },
  // Reports sub-routes
  { path: '/reports/sales-performance', label: 'Sales Performance', icon: '📈' },
  { path: '/reports/transactions', label: 'Service Transactions', icon: '📋' },
  { path: '/reports/vendor-statements', label: 'Vendor Statements', icon: '🧾' },
  { path: '/reports/invoice-splits', label: 'Customer Invoice Splits', icon: '🔀' },
  { path: '/reports/customer-survey', label: 'Customer Survey', icon: '📝' },
  { path: '/reports/lead-performance', label: 'Lead Performance', icon: '🎯' },
  { path: '/reports/equipment-register', label: 'Equipment Register', icon: '🔧' },
  { path: '/reports/whatsapp', label: 'WhatsApp Reports', icon: '💬' },
  // Email sub-routes
  { path: '/email/customers', label: 'Customer Emails', icon: '👤' },
  { path: '/email/leads', label: 'Lead Emails', icon: '🎯' },
  { path: '/email/candidates', label: 'Candidate Emails', icon: '👷' },
  // Peace of Sleep sub-routes
  { path: '/pos-leads', label: 'Leads', icon: '🎯' },
  // Hospitality sub-routes
  { path: '/hospitality-dashboard', label: 'Hospitality Dashboard', icon: '📊' },
  { path: '/hospitality-management', label: 'Hospitality Management', icon: '🏨' },
  // Operator + Profile
  { path: '/operator', label: 'Operator Home', icon: '🏠' },
  { path: '/profile', label: 'My Profile', icon: '👤' },
];
